package org.catharsis.spendtel.feature.presentation.expensereport

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.CalendarMonth
import androidx.compose.material.icons.outlined.Flag
import androidx.compose.material.icons.outlined.Lightbulb
import androidx.compose.material.icons.outlined.Repeat
import androidx.compose.material.icons.outlined.ShoppingBag
import androidx.compose.material.icons.outlined.TrendingUp
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.catharsis.spendtel.feature.data.FinancialGoal
import org.catharsis.spendtel.feature.data.GoalInsight
import org.catharsis.spendtel.feature.data.MoneyLeaks
import org.catharsis.spendtel.feature.data.ReportTrends
import org.catharsis.spendtel.feature.data.SpendingEquivalence
import org.catharsis.spendtel.feature.data.SpendingPatterns
import org.catharsis.spendtel.feature.data.formatCurrency
import org.catharsis.spendtel.feature.share.ShareImageCard
import org.catharsis.spendtel.feature.share.highlightShareContent
import org.catharsis.spendtel.feature.share.shareContent

@Composable
fun HighlightsSection(highlights: List<String>) {
    Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
        ReportSectionHeader(
            title = "Story highlights",
            subtitle = "Share-worthy one-liners from your month",
            accent = SpendTelReportColors.amber,
            icon = Icons.Outlined.Lightbulb,
        )
        highlights.forEachIndexed { index, highlight ->
            SoftInsightCard(accent = accentForIndex(index)) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.Top,
                ) {
                    Text(
                        text = highlight,
                        modifier = Modifier.weight(1f),
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.SemiBold,
                        color = SpendTelReportColors.ink,
                        lineHeight = 28.sp,
                    )
                    ShareCardActions(
                        content = highlightShareContent(highlight),
                        imageCard = ShareImageCard.Highlight(highlight),
                    )
                }
            }
        }
    }
}

@Composable
fun EquivalencesSection(
    equivalences: List<SpendingEquivalence>,
    currency: String,
) {
    Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
        ReportSectionHeader(
            title = "Spending equivalences",
            subtitle = "Playful ways to picture where your money went",
            accent = SpendTelReportColors.coral,
            icon = Icons.Outlined.ShoppingBag,
        )
        equivalences.forEachIndexed { index, equivalence ->
            val accent = accentForIndex(index + 1)
            GradientHeroCard(gradient = SpendTelGradients.storyWarm) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.Top,
                ) {
                    Column(
                        modifier = Modifier.weight(1f),
                        verticalArrangement = Arrangement.spacedBy(8.dp),
                    ) {
                        Text(
                            text = equivalence.sourceLabel.uppercase(),
                            style = MaterialTheme.typography.labelMedium,
                            color = Color.White.copy(alpha = 0.75f),
                            letterSpacing = 1.sp,
                        )
                        Text(
                            text = formatCurrency(equivalence.amount, currency),
                            style = MaterialTheme.typography.titleLarge,
                            fontWeight = FontWeight.Bold,
                            color = Color.White,
                        )
                        Text(
                            text = equivalence.comparison,
                            style = MaterialTheme.typography.headlineSmall,
                            fontWeight = FontWeight.Bold,
                            color = Color.White,
                            lineHeight = 30.sp,
                        )
                        Text(
                            text = equivalence.insight,
                            style = MaterialTheme.typography.bodyLarge,
                            color = Color.White.copy(alpha = 0.92f),
                            lineHeight = 24.sp,
                        )
                        Text(
                            text = "Estimates for illustration",
                            style = MaterialTheme.typography.labelSmall,
                            color = Color.White.copy(alpha = 0.65f),
                        )
                    }
                    ShareCardActions(
                        content = equivalence.shareContent(currency),
                        imageCard = ShareImageCard.Equivalence(equivalence, currency),
                    )
                }
            }
        }
    }
}

@Composable
fun GoalInsightsSection(insights: List<GoalInsight>) {
    Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
        ReportSectionHeader(
            title = "Goal-aware insights",
            subtitle = "Personalized for your financial goal",
            accent = SpendTelReportColors.violet,
            icon = Icons.Outlined.Flag,
        )
        insights.forEachIndexed { index, insight ->
            val accent = accentForIndex(index + 3)
            AccentSurfaceCard(accent = accent) {
                Text(
                    text = goalLabel(insight.goal),
                    style = MaterialTheme.typography.labelLarge,
                    fontWeight = FontWeight.Bold,
                    color = accent.darkenForText(),
                )
                Text(
                    text = insight.insight,
                    style = MaterialTheme.typography.bodyLarge,
                    color = SpendTelReportColors.ink,
                    lineHeight = 24.sp,
                )
                QuoteObservation(text = insight.suggestion, accent = accent)
            }
        }
    }
}

@Composable
fun SpendingPatternsSection(
    patterns: SpendingPatterns,
    currency: String,
) {
    Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
        ReportSectionHeader(
            title = "Your rhythms",
            subtitle = "When and where your spending shows up",
            accent = SpendTelReportColors.teal,
            icon = Icons.Outlined.CalendarMonth,
        )
        SoftInsightCard(accent = SpendTelReportColors.teal) {
            patterns.weekdayVsWeekend?.let {
                PatternRow(label = "Weekday vs weekend", value = it)
            }
            patterns.busiestSpendingDay?.let {
                PatternRow(label = "Busiest day", value = it)
            }
            patterns.convenienceShare?.let {
                PatternRow(label = "Convenience share", value = "${it.toInt()}%")
            }
            patterns.diningShare?.let {
                PatternRow(label = "Dining share", value = "${it.toInt()}%")
            }
            if (patterns.topMerchantsByShare.isNotEmpty()) {
                Text(
                    text = "Top merchants by share",
                    style = MaterialTheme.typography.titleSmall,
                    fontWeight = FontWeight.Bold,
                    color = SpendTelReportColors.ink,
                    modifier = Modifier.padding(top = 4.dp),
                )
                patterns.topMerchantsByShare.forEach { merchant ->
                    Column(
                        modifier = Modifier.fillMaxWidth(),
                        verticalArrangement = Arrangement.spacedBy(2.dp),
                    ) {
                        Text(
                            text = merchant.merchant,
                            style = MaterialTheme.typography.bodyMedium,
                            fontWeight = FontWeight.SemiBold,
                            color = SpendTelReportColors.ink,
                        )
                        Text(
                            text = "${merchant.percent.toInt()}% · ${formatCurrency(merchant.total, currency)}",
                            style = MaterialTheme.typography.labelLarge,
                            fontWeight = FontWeight.Bold,
                            color = SpendTelReportColors.teal.darkenForText(),
                        )
                    }
                }
            }
        }
    }
}

@Composable
fun ReportTrendsSection(trends: ReportTrends) {
    Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
        ReportSectionHeader(
            title = "Since your last story",
            subtitle = "How this report compares to your previous one",
            accent = SpendTelReportColors.indigo,
            icon = Icons.Outlined.TrendingUp,
        )
        SoftInsightCard(accent = SpendTelReportColors.indigo) {
            trends.spendingVsLastReport?.let {
                PatternRow(label = "Spending change", value = it)
            }
            trends.personalityShift?.let {
                PatternRow(label = "Personality shift", value = it)
            }
            trends.recurringTheme?.let {
                PatternRow(label = "Recurring theme", value = it)
            }
        }
    }
}

@Composable
fun FirstReportComparisonTeaser() {
    Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
        ReportSectionHeader(
            title = "Since your last story",
            subtitle = "Report-over-report comparisons",
            accent = SpendTelReportColors.indigo,
            icon = Icons.Outlined.TrendingUp,
        )
        SoftInsightCard(accent = SpendTelReportColors.indigo) {
            Text(
                text = "Your next report will compare against this one — shifts in spending, personality, and habits will show up here.",
                style = MaterialTheme.typography.bodyLarge,
                color = SpendTelReportColors.ink.copy(alpha = 0.9f),
                lineHeight = 24.sp,
            )
        }
    }
}

@Composable
fun MoneyLeaksSection(
    moneyLeaks: MoneyLeaks,
    currency: String,
) {
    Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
        ReportSectionHeader(
            title = "Money leaks",
            subtitle = "Subscriptions, fees, and recurring charges we spotted",
            accent = SpendTelReportColors.increase,
            icon = Icons.Outlined.Repeat,
        )
        SoftInsightCard(accent = SpendTelReportColors.increase) {
            moneyLeaks.summary?.let {
                Text(
                    text = it,
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    color = SpendTelReportColors.ink,
                )
            }
            moneyLeaks.totalMonthlySubscriptions?.let {
                PatternRow(
                    label = "Monthly subscriptions",
                    value = formatCurrency(it, currency),
                )
            }
            moneyLeaks.totalFees?.let {
                PatternRow(
                    label = "Fees & interest",
                    value = formatCurrency(it, currency),
                )
            }
            if (moneyLeaks.subscriptions.isNotEmpty()) {
                Text(
                    text = "Recurring charges",
                    style = MaterialTheme.typography.titleSmall,
                    fontWeight = FontWeight.Bold,
                    color = SpendTelReportColors.ink,
                    modifier = Modifier.padding(top = 8.dp),
                )
                moneyLeaks.subscriptions.forEach { subscription ->
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.Top,
                    ) {
                        Column(modifier = Modifier.weight(1f)) {
                            Text(
                                text = subscription.name,
                                style = MaterialTheme.typography.bodyMedium,
                                fontWeight = FontWeight.SemiBold,
                                color = SpendTelReportColors.ink,
                            )
                            Text(
                                text = "${subscription.frequency} · ${formatCurrency(subscription.annualized, currency)}/yr est.",
                                style = MaterialTheme.typography.labelSmall,
                                color = SpendTelReportColors.inkMuted,
                            )
                        }
                        Text(
                            text = formatCurrency(subscription.amount, currency),
                            style = MaterialTheme.typography.bodyMedium,
                            fontWeight = FontWeight.Bold,
                            color = SpendTelReportColors.increase,
                        )
                    }
                }
            }
            if (moneyLeaks.feesAndInterest.isNotEmpty()) {
                Text(
                    text = "Fees & interest",
                    style = MaterialTheme.typography.titleSmall,
                    fontWeight = FontWeight.Bold,
                    color = SpendTelReportColors.ink,
                    modifier = Modifier.padding(top = 8.dp),
                )
                moneyLeaks.feesAndInterest.forEach { fee ->
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                    ) {
                        Text(
                            text = fee.label,
                            style = MaterialTheme.typography.bodyMedium,
                            color = SpendTelReportColors.ink,
                            modifier = Modifier.weight(1f),
                        )
                        Text(
                            text = formatCurrency(fee.amount, currency),
                            style = MaterialTheme.typography.bodyMedium,
                            fontWeight = FontWeight.Bold,
                            color = SpendTelReportColors.increase,
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun PatternRow(label: String, value: String) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(4.dp),
    ) {
        Text(
            text = label,
            style = MaterialTheme.typography.labelLarge,
            fontWeight = FontWeight.SemiBold,
            color = SpendTelReportColors.inkMuted,
        )
        Text(
            text = value,
            style = MaterialTheme.typography.bodyMedium,
            color = SpendTelReportColors.ink,
            lineHeight = 22.sp,
        )
    }
}

private fun goalLabel(goalId: String): String {
    return FinancialGoal.fromId(goalId)?.title
        ?: goalId.replace('_', ' ').replaceFirstChar { it.titlecase() }
}

private fun Color.darkenForText(): Color {
    return Color(red = red * 0.75f, green = green * 0.75f, blue = blue * 0.75f, alpha = alpha)
}
